"use client";

import dynamic from "next/dynamic";

import { isSeoSurfacePushCaptureEnabled } from "@/lib/runtime-flags";

// Flag-gated, lazy mount for the SEO-surface free web-push capture (2026-07-21).
//
// BORN DARK: NEXT_PUBLIC_SEO_SURFACE_PUSH_CAPTURE_ENABLED defaults to "0", so this
// returns null and NEVER imports the slide-in chunk — the capture adds zero bytes
// to the page (nothing in first-load JS, nothing in the SSR HTML) and cannot
// affect SEO, CLS, or the CWV budget (rules 28/49/62/66) while off. It goes live
// only after the daily-loop D7 retention gate proves out (~07-23+, owner decision).
//
// When enabled it loads the slide-in client-side only (ssr:false, loading:null),
// so it still never renders on the server and never enters the indexable HTML.
// This wrapper is the single place every SEO surface mounts the capture, so there
// is exactly one instance per surface (rule 67 — pinned by the one-instance test).

const SeoSurfacePushCapture = dynamic(() => import("./SeoSurfacePushCapture"), {
  ssr: false,
  loading: () => null,
});

export default function SeoSurfacePushCaptureMount({
  surface,
  lang = "hi",
  trigger,
}: {
  surface: string;
  lang?: "en" | "hi";
  /**
   * `"value_moment"` on surfaces that produce a result (the ask waits for it);
   * omit on content hubs, which have no result to wait for.
   */
  trigger?: "engagement" | "value_moment";
}) {
  if (!isSeoSurfacePushCaptureEnabled()) return null;
  return <SeoSurfacePushCapture surface={surface} lang={lang} trigger={trigger} />;
}
